/**
 * Marketing attribution capture.
 *
 * On page load this captures UTM parameters, ad-platform click IDs and the
 * external referrer, classifies the visit into a marketing channel, and
 * persists a first-touch record (never overwritten) and a last-touch record
 * (updated on each new attributed visit) in localStorage. This lets signup
 * and purchase events be attributed to the original acquisition source even
 * when the conversion happens on a later visit.
 */

export type MarketingChannel =
  | 'paid_search'
  | 'paid_social'
  | 'paid_other'
  | 'organic_search'
  | 'organic_social'
  | 'email'
  | 'affiliate'
  | 'referral'
  | 'direct';

export interface Attribution {
  source: string;
  medium: string;
  campaign?: string;
  term?: string;
  content?: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  referrer?: string;
  landingPage: string;
  channel: MarketingChannel;
  capturedAt: string;
}

const FIRST_TOUCH_KEY = 'attribution_first_touch';
const LAST_TOUCH_KEY = 'attribution_last_touch';

const SEARCH_ENGINE_HOSTS = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'ecosia.'];
const SOCIAL_HOSTS = [
  'facebook.', 'instagram.', 'twitter.', 't.co', 'x.com', 'tiktok.',
  'linkedin.', 'youtube.', 'reddit.', 'threads.'
];
const SOCIAL_SOURCES = [
  'facebook', 'instagram', 'meta', 'twitter', 'x', 'tiktok', 'linkedin',
  'youtube', 'reddit', 'threads'
];
const SEARCH_SOURCES = ['google', 'bing', 'yahoo', 'duckduckgo'];
const PAID_MEDIUMS = [
  'cpc', 'ppc', 'paid', 'paid_social', 'paid-social', 'cpm', 'display', 'retargeting'
];

const hostMatches = (hostname: string, patterns: string[]): boolean =>
  patterns.some(pattern => hostname.includes(pattern));

const classifyChannel = (attribution: {
  source: string;
  medium: string;
  gclid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  referrerHost?: string;
  affiliateRef?: string;
}): MarketingChannel => {
  const source = attribution.source.toLowerCase();
  const medium = attribution.medium.toLowerCase();

  if (attribution.gclid || attribution.msclkid) return 'paid_search';
  if (attribution.ttclid) return 'paid_social';

  if (PAID_MEDIUMS.includes(medium)) {
    if (SOCIAL_SOURCES.includes(source) || hostMatches(source, SOCIAL_HOSTS)) return 'paid_social';
    if (SEARCH_SOURCES.includes(source)) return 'paid_search';
    return 'paid_other';
  }

  if (medium === 'email' || source === 'email') return 'email';
  if (medium === 'affiliate' || attribution.affiliateRef) return 'affiliate';
  if (medium === 'social' || SOCIAL_SOURCES.includes(source)) return 'organic_social';
  if (attribution.fbclid) return 'organic_social';

  if (attribution.referrerHost) {
    if (hostMatches(attribution.referrerHost, SEARCH_ENGINE_HOSTS)) return 'organic_search';
    if (hostMatches(attribution.referrerHost, SOCIAL_HOSTS)) return 'organic_social';
    return 'referral';
  }

  if (source || medium !== 'none') return 'referral';
  return 'direct';
};

const readStored = (key: string): Attribution | null => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Attribution) : null;
  } catch {
    return null;
  }
};

const writeStored = (key: string, attribution: Attribution): void => {
  try {
    localStorage.setItem(key, JSON.stringify(attribution));
  } catch {
    // Storage unavailable (private mode / quota) - attribution is best-effort
  }
};

/**
 * Capture attribution for the current page load and update the stored
 * first-touch / last-touch records. Call once at app startup.
 */
export const captureAttribution = (): Attribution | null => {
  try {
    const params = new URLSearchParams(window.location.search);

    const utmSource = params.get('utm_source') || '';
    const utmMedium = params.get('utm_medium') || '';
    const utmCampaign = params.get('utm_campaign') || undefined;
    const utmTerm = params.get('utm_term') || undefined;
    const utmContent = params.get('utm_content') || undefined;
    const gclid = params.get('gclid') || undefined;
    const fbclid = params.get('fbclid') || undefined;
    const ttclid = params.get('ttclid') || undefined;
    const msclkid = params.get('msclkid') || undefined;
    const affiliateRef = params.get('ref') || undefined;

    // Ignore internal navigation referrers - only external referrers count
    let referrerHost: string | undefined;
    if (document.referrer) {
      try {
        const host = new URL(document.referrer).hostname;
        if (host && host !== window.location.hostname) {
          referrerHost = host;
        }
      } catch {
        // Malformed referrer - ignore
      }
    }

    const hasSignal = Boolean(
      utmSource || utmMedium || utmCampaign || gclid || fbclid || ttclid ||
      msclkid || affiliateRef || referrerHost
    );

    const source = utmSource ||
      (gclid ? 'google' : msclkid ? 'bing' : fbclid ? 'facebook' : ttclid ? 'tiktok' : referrerHost || '');
    const medium = utmMedium ||
      (gclid || msclkid || ttclid ? 'cpc' : fbclid ? 'social' : referrerHost ? 'referral' : 'none');

    const attribution: Attribution = {
      source: source || 'direct',
      medium,
      campaign: utmCampaign,
      term: utmTerm,
      content: utmContent,
      gclid,
      fbclid,
      ttclid,
      msclkid,
      referrer: document.referrer || undefined,
      landingPage: window.location.pathname + window.location.search,
      channel: classifyChannel({ source, medium, gclid, fbclid, ttclid, msclkid, referrerHost, affiliateRef }),
      capturedAt: new Date().toISOString()
    };

    if (!readStored(FIRST_TOUCH_KEY)) {
      writeStored(FIRST_TOUCH_KEY, attribution);
    }
    if (hasSignal) {
      writeStored(LAST_TOUCH_KEY, attribution);
    }

    return attribution;
  } catch {
    return null;
  }
};

export interface StoredAttribution {
  firstTouch: Attribution | null;
  lastTouch: Attribution | null;
}

export const getAttribution = (): StoredAttribution => ({
  firstTouch: readStored(FIRST_TOUCH_KEY),
  lastTouch: readStored(LAST_TOUCH_KEY)
});

/**
 * Flatten stored attribution into GA4 event parameters. Register these as
 * custom dimensions in GA4 (Admin > Custom definitions) to see them in reports.
 */
export const getGaAttributionParams = (): Record<string, string> => {
  const { firstTouch, lastTouch } = getAttribution();
  const params: Record<string, string> = {};

  if (firstTouch) {
    params.first_touch_source = firstTouch.source;
    params.first_touch_medium = firstTouch.medium;
    params.first_touch_channel = firstTouch.channel;
    if (firstTouch.campaign) params.first_touch_campaign = firstTouch.campaign;
    params.first_touch_landing_page = firstTouch.landingPage;
  }
  if (lastTouch) {
    params.last_touch_source = lastTouch.source;
    params.last_touch_medium = lastTouch.medium;
    params.last_touch_channel = lastTouch.channel;
    if (lastTouch.campaign) params.last_touch_campaign = lastTouch.campaign;
  }

  return params;
};
